import { useQuery } from "@apollo/client";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { Alert, Box, Button, CircularProgress, FormControlLabel, Link, Paper, Switch, Typography } from "@mui/material";
import { useCallback, useId, useMemo, useState } from "react";
import { Title, useCanAccess, useTranslate } from "react-admin";
import { Link as RouterLink, useLocation, useParams } from "react-router-dom";

import { ReportCoverageWarnings, ReportUnavailable } from "../components/SalesforceReportAvailability";
import { SalesforceReportChartBody } from "../components/SalesforceReportChartBody";
import { ReportTableView } from "../components/SalesforceReportTableView";
import { LOGISTICS_ROUTE, ResourceName } from "../constants/resources";
import {
  SalesforceReportDocument,
  type SalesforceReportQuery,
  type SalesforceReportQueryVariables,
  type SalesforceReportSectionFragment,
} from "../generated/graphql";
import { formatCount, SALESFORCE_CHART_COLORS, salesforceSyncedAtFormat } from "../lib/salesforceDashboard";
import {
  findReportTab,
  isReportRequestError,
  parseSalesforceReportSearch,
  type ReportContext,
  SALESFORCE_REPORT_TABS,
  salesforceReportFilterUrl,
  salesforceReportPath,
  salesforceReportUrl,
} from "../lib/salesforceReport";
import {
  defaultReportTableOptions,
  describeReportFilters,
  formatSummaryValue,
  type ReportFilterPath,
  type ReportPage,
  reportTableLabels,
  type ReportTableOptions,
} from "../lib/salesforceReportTable";
import { color, space } from "../theme/designTokens";

const colors = SALESFORCE_CHART_COLORS;
const ICON_TILE_SIZE_PX = 32;
const CHART_HEIGHT_PX = 320;
const FILTER_PANEL_WIDTH_PX = 280;

const textSx = { fontSize: "13px", color: colors.text } as const;

const FilterList = ({ items }: { items: string[] }) => (
  <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", ...textSx }}>
    {items.map(item => (
      <Box component="li" key={item} sx={{ py: `${space.xs}px`, overflowWrap: "anywhere" }}>
        {item}
      </Box>
    ))}
  </Box>
);

const FilterPanel = ({ page }: { page: ReportPage }) => {
  const titleId = useId();
  const translate = useTranslate();
  const filters = useMemo(() => describeReportFilters(translate, page), [page, translate]);
  const own = [...(filters.dateRange ? [filters.dateRange] : []), ...filters.filters];

  return (
    <Paper
      aria-labelledby={titleId}
      component="aside"
      elevation={0}
      sx={{
        flex: "none",
        width: { xs: "100%", lg: `${FILTER_PANEL_WIDTH_PX}px` },
        p: `${space.lg}px`,
        backgroundColor: colors.cardBackground,
        border: `1px solid ${colors.cardBorder}`,
        borderRadius: "4px",
      }}
    >
      <Typography component="h2" id={titleId} sx={{ mb: `${space.sm}px`, fontSize: "14px", fontWeight: 700 }}>
        {translate("salesforceReports.report.filters")}
      </Typography>
      {own.length === 0 ? (
        <Typography sx={textSx}>{translate("salesforceReports.report.noFilters")}</Typography>
      ) : (
        <FilterList items={own} />
      )}
      {filters.filterLogic && <Typography sx={{ ...textSx, mt: `${space.sm}px` }}>{filters.filterLogic}</Typography>}
      {filters.linked.length > 0 && (
        <>
          <Typography
            component="h3"
            sx={{ mt: `${space.lg}px`, mb: `${space.sm}px`, fontSize: "14px", fontWeight: 700 }}
          >
            {translate("salesforceReports.report.linkedFilters")}
          </Typography>
          <FilterList items={filters.linked} />
        </>
      )}
    </Paper>
  );
};

const SummaryStrip = ({ page }: { page: ReportPage }) => {
  const translate = useTranslate();
  const labels = useMemo(() => reportTableLabels(translate, page), [page, translate]);
  const grand = page.groups.find(group => group.down.length === 0 && group.across.length === 0);
  const items = [
    { key: "count", label: translate("salesforceReports.report.totalRecords"), value: formatCount(grand?.count ?? 0) },
    ...page.summaries.map((summary, index) => ({
      key: `summary-${index}`,
      label: labels.summary(summary),
      value: formatSummaryValue(summary, grand?.values[index] ?? null),
    })),
  ];

  return (
    <Box
      component="dl"
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: `${space.sm}px ${space.xl}px`,
        m: 0,
        mb: `${space.lg}px`,
        p: `${space.lg}px ${space.xl}px`,
        backgroundColor: colors.cardBackground,
        border: `1px solid ${colors.cardBorder}`,
        borderRadius: "4px",
      }}
    >
      {items.map(item => (
        <Box key={item.key} sx={{ display: "flex", flexDirection: "column-reverse", minWidth: 0 }}>
          <Box component="dt" sx={{ fontSize: "12px", color: colors.textWeak }}>
            {item.label}
          </Box>
          <Box component="dd" sx={{ m: 0, fontSize: "20px", color: colors.textStrong }}>
            {item.value}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const TOGGLES = ["rowCounts", "detailRows", "subtotals", "grandTotal"] as const;

type ReportSection = Omit<SalesforceReportSectionFragment, "__typename">;

const ReportChart = ({ page, title }: { page: ReportSection; title: string }) => {
  const translate = useTranslate();
  if (!page.chart) return null;
  return (
    <Paper
      aria-label={translate("salesforceReports.report.chartLabel", { title })}
      component="section"
      elevation={0}
      sx={{
        display: "flex",
        height: `${CHART_HEIGHT_PX}px`,
        backgroundColor: colors.cardBackground,
        borderRadius: "4px",
        mb: `${space.lg}px`,
        p: `${space.lg}px`,
        border: `1px solid ${colors.cardBorder}`,
      }}
    >
      <Box sx={{ display: "flex", flex: 1, flexDirection: "column", minWidth: 0, minHeight: 0 }}>
        <ReportCoverageWarnings missingFields={page.chartMissingFields} pendingObjects={page.chartPendingObjects} />
        {page.chartUnavailable ? (
          <ReportUnavailable reason={page.chartUnavailable} />
        ) : (
          <SalesforceReportChartBody
            component={{
              chart: page.chart,
              groupings: page.chartGroupings,
              measures: page.chartMeasures,
              points: page.chartPoints,
              sortByValue: false,
              showPercentages: false,
              source: page.source,
              showValues: page.showValues,
              sortLegendValues: page.sortLegendValues,
            }}
          />
        )}
      </Box>
    </Paper>
  );
};

const ReportContent = ({
  page,
  title,
  filterPath,
  recordLinks = true,
  includeChart = true,
}: {
  page: ReportSection;
  title: string;
  filterPath: ReportFilterPath;
  recordLinks?: boolean;
  includeChart?: boolean;
}) => {
  const translate = useTranslate();
  const [options, setOptions] = useState<ReportTableOptions>(() => ({
    ...defaultReportTableOptions(page),
    recordLinks,
  }));
  return (
    <>
      <ReportCoverageWarnings
        unreadableFields={page.unreadableFields}
        missingFields={page.missingFields}
        pendingObjects={page.pendingObjects}
      />
      {page.unavailable ? (
        <>
          {includeChart && <ReportChart page={page} title={title} />}
          <ReportUnavailable reason={page.unavailable} />
          <Box sx={{ mt: `${space.lg}px` }}>
            <FilterPanel page={page} />
          </Box>
        </>
      ) : (
        <>
          <SummaryStrip page={page} />
          {includeChart && <ReportChart page={page} title={title} />}
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: `${space.lg}px` }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: colors.cardBackground,
                  border: `1px solid ${colors.cardBorder}`,
                  borderRadius: "4px",
                }}
              >
                <ReportTableView filterPath={filterPath} options={{ ...options, recordLinks }} page={page} />
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: `${space.sm}px ${space.lg}px`,
                    px: `${space.lg}px`,
                    py: `${space.sm}px`,
                    backgroundColor: colors.headerBackground,
                    borderTop: `1px solid ${colors.cardBorder}`,
                  }}
                >
                  {TOGGLES.map(toggle => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={options[toggle]}
                          disabled={toggle === "detailRows" && page.columns.length === 0}
                          onChange={(_, checked) => setOptions(current => ({ ...current, [toggle]: checked }))}
                          size="small"
                        />
                      }
                      key={toggle}
                      label={translate(`salesforceReports.report.toggles.${toggle}`)}
                      slotProps={{ typography: { fontSize: "13px" } }}
                    />
                  ))}
                </Box>
              </Paper>
              {page.hasMoreRows && (
                <Alert severity="info" sx={{ mt: `${space.lg}px` }}>
                  {translate("salesforceReports.report.rowLimit", { limit: formatCount(page.rowLimit) })}
                </Alert>
              )}
            </Box>
            <FilterPanel page={page} />
          </Box>
        </>
      )}
    </>
  );
};

const SalesforceReportContent = ({ reportId, context }: { reportId: string; context: ReportContext }) => {
  const translate = useTranslate();
  const { canAccess, isPending } = useCanAccess({ action: "list", resource: ResourceName.Crm });
  const recordLinks = context !== "logistics" || (canAccess === true && !isPending);
  const { search } = useLocation();
  const link = useMemo(() => parseSalesforceReportSearch(search), [search]);
  const { data, loading, error, refetch } = useQuery<SalesforceReportQuery, SalesforceReportQueryVariables>(
    SalesforceReportDocument,
    {
      variables: {
        reportId,
        component: link.component ?? undefined,
        values: link.component ? link.values : undefined,
        columnFilters: link.columnFilters.length > 0 ? link.columnFilters : undefined,
      },
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
    }
  );
  const filterPath = useCallback<ReportFilterPath>(
    (field, value) => salesforceReportFilterUrl(reportId, link, field, value, context),
    [link, reportId, context]
  );
  const page = data?.salesforceReport;
  // The server refuses a report or click it does not know with an error of its own, unlike a failed load.
  const notFound = error !== undefined && isReportRequestError(error);
  const title = translate(`salesforceReports.reports.${reportId}.title`, { _: reportId });
  const description = translate(`salesforceReports.reports.${reportId}.description`, { _: "" });
  const backPath =
    context === "logistics"
      ? LOGISTICS_ROUTE
      : salesforceReportPath((findReportTab(link.dashboard ?? undefined) ?? SALESFORCE_REPORT_TABS[0]).slug);
  const syncedAt = page?.lastSyncedAt ? salesforceSyncedAtFormat.format(new Date(page.lastSyncedAt)) : null;

  return (
    <Box sx={{ p: `${space.xl}px` }}>
      <Title title={title} />
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: `${space.xl}px`,
          mb: `${space.lg}px`,
          p: `${space.lg}px ${space.xl}px`,
          backgroundColor: colors.headerBackground,
          border: `1px solid ${colors.cardBorder}`,
          borderRadius: "4px",
          color: colors.text,
        }}
      >
        <Box sx={{ display: "flex", gap: `${space.lg}px`, minWidth: 0 }}>
          <Box
            aria-hidden
            sx={{
              display: "flex",
              flex: "none",
              alignItems: "center",
              justifyContent: "center",
              width: `${ICON_TILE_SIZE_PX}px`,
              height: `${ICON_TILE_SIZE_PX}px`,
              backgroundColor: color.primary,
              borderRadius: "4px",
              color: color.white,
            }}
          >
            <AssessmentOutlinedIcon fontSize="small" />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: `${space.xs}px`, minWidth: 0 }}>
            {page && (
              <Typography sx={{ fontSize: "12px", lineHeight: 1.25, color: colors.text }}>
                {translate("salesforceReports.report.overline", {
                  type: translate(`salesforceReports.reportTypes.${page.type}`, { _: page.type }),
                })}
              </Typography>
            )}
            <Typography
              component="h1"
              sx={{ m: 0, fontSize: "18px", fontWeight: 700, lineHeight: 1.25, color: colors.text }}
            >
              {title}
            </Typography>
            {description !== "" && <Typography sx={{ fontSize: "12px", color: colors.text }}>{description}</Typography>}
            {syncedAt && (
              <Typography sx={{ fontSize: "12px", color: colors.text }}>
                {translate("salesforceDashboard.asOf", { date: syncedAt })}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: `${space.md}px` }}>
          <Link
            component={RouterLink}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: `${space.xs}px`,
              fontSize: "13px",
              color: colors.link,
            }}
            to={backPath}
            underline="hover"
          >
            <ArrowBackOutlinedIcon fontSize="inherit" />
            {translate("salesforceReports.report.back")}
          </Link>
          {context === "logistics" && (link.component || link.columnFilters.length > 0) && (
            <Button
              component={RouterLink}
              to={salesforceReportUrl(reportId, { dashboard: link.dashboard ?? undefined }, context)}
            >
              {translate("salesforceReports.report.clearFilters")}
            </Button>
          )}
          <Button
            disabled={loading}
            onClick={() => void refetch().catch(() => undefined)}
            startIcon={<RefreshOutlinedIcon />}
            sx={{
              height: "32px",
              backgroundColor: colors.cardBackground,
              border: `1px solid ${colors.cardBorder}`,
              borderRadius: "4px",
              fontSize: "13px",
              textTransform: "none",
              color: colors.link,
              "&:hover": { backgroundColor: colors.headerBackground, border: `1px solid ${colors.cardBorder}` },
            }}
            variant="outlined"
          >
            {translate("salesforceDashboard.refresh")}
          </Button>
        </Box>
      </Box>

      {loading && !page && <CircularProgress aria-label={translate("salesforceDashboard.loading")} />}
      {error && (
        <Alert severity="error" sx={{ mb: `${space.lg}px` }}>
          {translate(notFound ? "salesforceReports.report.notFound" : "salesforceDashboard.loadError")}
        </Alert>
      )}

      {context !== "logistics" && page?.lastSyncedAt === null && !page.blocks?.length && !page.unavailable && (
        <Paper variant="outlined" sx={{ p: `${space.xl}px` }}>
          <Typography component="h2" variant="h6">
            {translate("salesforceDashboard.noImportTitle")}
          </Typography>
          <Typography color="text.secondary">{translate("salesforceDashboard.noImportDescription")}</Typography>
        </Paper>
      )}

      {page &&
        (page.lastSyncedAt || context === "logistics" || page.unavailable || page.blocks?.length) &&
        (page.blocks?.length ? (
          <>
            <ReportChart page={page} title={title} />
            {page.blocks.map(block => (
              <Box component="section" key={`${reportId}${search}${block.blockKey}`} sx={{ mb: `${space.xl}px` }}>
                <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
                  {translate(`salesforceReports.blocks.${block.blockKey}`)}
                </Typography>
                <ReportContent
                  page={block}
                  title={title}
                  includeChart={false}
                  recordLinks={recordLinks}
                  filterPath={page.selectedBlock ? filterPath : () => null}
                />
              </Box>
            ))}
          </>
        ) : (
          <ReportContent
            key={`${reportId}${search}`}
            filterPath={filterPath}
            page={page}
            title={title}
            recordLinks={recordLinks}
          />
        ))}
    </Box>
  );
};

export const SalesforceReportPage = ({ context = "general" }: { context?: ReportContext }) => {
  const { reportId } = useParams<{ reportId: string }>();
  return reportId ? <SalesforceReportContent reportId={reportId} context={context} /> : null;
};
